import { BriefcaseBusiness, House, LocateFixed, MapPin } from "lucide-react";
import ResponsiveModal from "./ResponsiveModal";
import { Button } from "./ui/button";
import DeliveryGuy from "../../public/deliveryGuy.json";
import Lottie from "lottie-react";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AddressType } from "@/types/address";
import { useLocate } from "@/hooks/useLocate";

interface AddressModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (address: AddressType) => void;
  initialAddress?: AddressType;
}

export default function AddressModal({
  open,
  onClose,
  onSubmit,
  initialAddress,
}: AddressModalProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm();

  const { i18n, t } = useTranslation();

  const { locate } = useLocate();
  const [customLabel, setCustomLabel] = useState(false);

  useEffect(() => {
    if (open) {
      if (initialAddress) {
        reset({
          address: initialAddress.address,
          street: initialAddress.street,
          streetNumber: initialAddress.streetNumber,
          label: initialAddress.label,
        });

        const isCustom = !["home", "work"].includes(initialAddress.label);
        setCustomLabel(isCustom);
      } else {
        // Reset to empty values for new address
        reset({
          address: "",
          street: "",
          streetNumber: "",
          label: "",
        });
        setCustomLabel(false);
      }
    }
  }, [open, initialAddress, reset]);

  const renderContent = () => {
    return (
      <form
        onSubmit={handleSubmit((data) => {
          if (initialAddress) {
            onSubmit({ ...data, id: initialAddress.id } as AddressType);
          } else {
            onSubmit({ ...data } as AddressType);
          }
        })}
        className="flex flex-col min-h-[400px] max-h-[85vh] p-6 sm:p-8 gap-4"
        dir={i18n.dir() === "rtl" ? "rtl" : "ltr"}
      >
        {/* Header */}
        <div className="text-center space-y-2 shrink-0">
          <h1 className="text-2xl font-bold text-gray-900">
            {initialAddress ? t("edit_address") : t("add_new_address")}
          </h1>
        </div>

        {/* Address Input */}
        <div className="flex flex-col gap-1 shrink-0">
          <label
            htmlFor="address"
            className="text-sm font-medium text-gray-700"
          >
            {t("address")}
          </label>
          <div className="relative w-full">
            <input
              autoComplete="off"
              {...register("address", { required: "address is required" })}
              placeholder={t("enter_address")}
              className="w-full rounded-lg border border-gray-300 py-2 ps-3 pe-10 focus:outline-none focus:ring-2 focus:ring-primary"
            />

            <LocateFixed
              className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-500 cursor-pointer hover:text-primary transition-all duration-300"
              size={20}
              onClick={async () => {
                const { address, street, streetNumber } = await locate();
                reset({
                  address: address,
                  street: street,
                  streetNumber: streetNumber,
                });
              }}
            />
          </div>
          {errors.address && (
            <p className="text-red-500 text-sm">
              {errors.address.message as string}
            </p>
          )}
        </div>

        {/* Street and Street Number */}
        <div className="flex flex-row gap-4 shrink-0">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="street"
              className="text-sm font-medium text-gray-700"
            >
              {t("street")}
            </label>
            <input
              autoComplete="off"
              {...register("street", { required: "street is required" })}
              placeholder={t("enter_street")}
              className="w-full rounded-lg border border-gray-300 py-2 ps-3 pe-10 focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {errors.street && (
              <p className="text-red-500 text-sm">
                {errors.street.message as string}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="streetNumber"
              className="text-sm font-medium text-gray-700"
            >
              {t("street_number")}
            </label>
            <input
              autoComplete="off"
              {...register("streetNumber", {
                required: "streetNumber is required",
              })}
              placeholder={t("enter_street_number")}
              className="w-full rounded-lg border border-gray-300 py-2 ps-3 pe-10 focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {errors.streetNumber && (
              <p className="text-red-500 text-sm">
                {errors.streetNumber.message as string}
              </p>
            )}
          </div>
        </div>

        {/* Label Selection */}
        <Controller
          name="label"
          control={control}
          rules={{ required: "Label is required" }}
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-2 shrink-0">
              <h3 className="text-sm font-medium text-gray-700">
                {t("label") || "Label"}
              </h3>
              <div className="flex gap-2">
                {[
                  { value: "home", icon: <House className="size-4" /> },
                  {
                    value: "work",
                    icon: <BriefcaseBusiness className="size-4" />,
                  },
                  { value: "other", icon: <MapPin className="size-4" /> },
                ].map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      if (option.value === "other") {
                        setCustomLabel(true);
                        field.onChange(""); // Clear the field for custom input
                      } else {
                        setCustomLabel(false);
                        field.onChange(option.value);
                      }
                    }}
                    className={`px-4 py-1 flex justify-center items-center gap-1 border rounded-full cursor-pointer transition-all duration-300 ${
                      (option.value === "other" && customLabel) ||
                      (field.value === option.value && !customLabel)
                        ? "bg-primary text-white border-primary"
                        : "bg-gray-50 border-gray-200 hover:border-gray-300 hover:bg-gray-100"
                    }`}
                  >
                    {option.icon}
                    <span className="text-sm capitalize">
                      {t(option.value) || option.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Custom Label Input */}
              {customLabel && (
                <div className="mt-2">
                  <input
                    value={field.value || ""}
                    onChange={field.onChange}
                    className="w-full rounded-lg border border-gray-300 py-2 ps-3 pe-3 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder={
                      t("enter_custom_label") || "Enter custom label"
                    }
                  />
                </div>
              )}

              {fieldState.error && (
                <p className="text-red-500 text-sm">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />

        {/* Lottie Animation */}
        <div className="w-full flex-1 mx-auto min-h-0">
          <Lottie
            className="w-full h-full"
            animationData={DeliveryGuy}
            loop={true}
            autoplay={true}
          />
        </div>

        {/* Submit Button */}
        <Button size="lg" className="mt-auto shrink-0 mb-2" type="submit">
          {t("save")}
        </Button>
      </form>
    );
  };

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      renderContent={renderContent}
    />
  );
}
